import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { FlashbarProps } from "@amzn/awsui-components-react/polaris";
import {
  getFromLocalStorage,
  writeToLocalStorage,
} from "../../util/localStorageUtils";
import logger from "../../art-logger-config";

interface CacheableNotificationState {
  notificationItems: Record<number, FlashbarProps.MessageDefinition>;
}

function getInitialPreferences() {
  const cacheableNotificationInitialState: CacheableNotificationState = {
    notificationItems: {},
  };

  try {
    const localPrefsString = getFromLocalStorage("NOTIFICATION_PREFERENCES");
    if (localPrefsString !== null) {
      return JSON.parse(localPrefsString) as CacheableNotificationState;
    }
  } catch (e: unknown) {
    const errorMessage = e instanceof Error ? e.message : String(e);
    logger.error(
      `Failed to load initial preferences for cacheable notification: ${errorMessage}`
    );
  }
  return cacheableNotificationInitialState;
}

const cacheableNotification = createSlice({
  name: "cacheableNotification",
  initialState: getInitialPreferences(),
  reducers: {
    addCacheableNotification(
      state: CacheableNotificationState,
      action: PayloadAction<{
        key: number;
        message: FlashbarProps.MessageDefinition;
      }>
    ) {
      state.notificationItems[action.payload.key] = action.payload.message;
      writeToLocalStorage(
        "NOTIFICATION_PREFERENCES",
        JSON.stringify({ notificationItems: state.notificationItems })
      );
    },
    removeCacheableNotification(
      state: CacheableNotificationState,
      action: PayloadAction<number>
    ) {
      delete state.notificationItems[action.payload];
      writeToLocalStorage(
        "NOTIFICATION_PREFERENCES",
        JSON.stringify({ notificationItems: state.notificationItems })
      );
    },
    clearCacheableNotifications(state: CacheableNotificationState) {
      state.notificationItems = {};
      writeToLocalStorage(
        "NOTIFICATION_PREFERENCES",
        JSON.stringify({ notificationItems: state.notificationItems })
      );
    },
  },
});

export const {
  addCacheableNotification,
  removeCacheableNotification,
  clearCacheableNotifications,
} = cacheableNotification.actions;

export default cacheableNotification.reducer;

export function createCacheableNotificationDefinition(
  type: FlashbarProps.Type,
  header: string,
  dismissible: boolean,
  key: number,
  content?: string
) {
  return addCacheableNotification({
    key: key,
    message: {
      type: type,
      header: header,
      dismissible: dismissible,
      content: content,
    },
  });
}
